// TextTabs — plain-text tabs (the approved Bibliotek reader-tab look): the
// ARIA tabs pattern with one Tab stop and arrow / Home / End keys. Moved out
// of the Journal (D41) so the ViewHeader's `nav` slot has a shared tab row;
// the interim tabs until the canonical Tabs primitive (design-system census,
// wave 2) replaces it and DetailWorkbench's identical `dw-reader-tab`.

import type { KeyboardEvent, ReactNode } from "react";
import "./TextTabs.css";

export type TextTab<T extends string> = {
    readonly value: T;
    readonly label: string;
};

export const TextTabs = <T extends string>({
    tabs,
    value,
    onChange,
    ariaLabel,
}: {
    readonly tabs: readonly TextTab<T>[];
    readonly value: T;
    readonly onChange: (value: T) => void;
    readonly ariaLabel: string;
}): ReactNode => {
    // The ARIA tabs pattern: one Tab stop (the selected tab); arrow keys,
    // Home and End move the selection and focus within the row. Widget
    // keys on a focused tablist, not a chrome chord.
    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        const at = tabs.findIndex((tab) => tab.value === value);
        const next = tabKeyTarget(event.key, at, tabs.length);
        const tab = next === undefined ? undefined : tabs[next];
        if (next === undefined || tab === undefined) return;
        event.preventDefault();
        onChange(tab.value);
        event.currentTarget
            .querySelectorAll<HTMLButtonElement>('[role="tab"]')
            .item(next)
            ?.focus();
    };
    return (
        <div
            className="bilda-text-tabs"
            role="tablist"
            aria-label={ariaLabel}
            onKeyDown={onKeyDown}
        >
            {tabs.map((tab) => (
                <button
                    key={tab.value}
                    type="button"
                    role="tab"
                    aria-selected={tab.value === value}
                    tabIndex={tab.value === value ? 0 : -1}
                    className={`bilda-text-tabs__tab${tab.value === value ? " bilda-text-tabs__tab--active" : ""}`}
                    onClick={() => onChange(tab.value)}
                >
                    {tab.label}
                </button>
            ))}
        </div>
    );
};

// The tab an arrow / Home / End key moves to (wrapping), or none.
const tabKeyTarget = (
    key: string,
    at: number,
    count: number,
): number | undefined => {
    switch (key) {
        case "ArrowRight":
            return (at + 1) % count;
        case "ArrowLeft":
            return (at - 1 + count) % count;
        case "Home":
            return 0;
        case "End":
            return count - 1;
        default:
            return undefined;
    }
};
